import { useEffect, useRef, useState } from 'react';

// ─────────────────────────────────────────────────────────────────────────────
// useWaterRipples
//
// Real-time water simulation on the GPU — a dependency-free port of the
// jQuery Ripples algorithm (sirxemic, MIT) used on techlion.co.ke.
//
// Instead of drawing a fixed number of analytic rings, a height field lives in
// a float texture. Every simulation step each texel is pulled toward the
// average of its neighbours, so waves actually propagate, interfere, reflect
// off edges and die out naturally. Pointer movement just "drops" energy into
// that field, which is why every touch point responds instantly.
//
// Rendering refracts the background image through the surface normals and
// adds a specular highlight on the wave crests.
// ─────────────────────────────────────────────────────────────────────────────

const DEFAULTS = {
  resolution: 512, // simulation grid (techlion: 500)
  dropRadius: 20, // px, pointer move
  perturbance: 0.04, // refraction strength (techlion: 0.04)
  moveStrength: 0.01, // energy per move drop (plugin default)
  clickStrength: 0.14, // energy per click / tap (plugin default)
  ambient: true, // occasional idle drops so visitors notice it's interactive
};

const STEP_MS = 1000 / 60; // fixed 60Hz sim — same wave speed on 60/120/144Hz screens
const MAX_STEPS_PER_FRAME = 3;
const MAX_QUEUED_DROPS = 96;
const MAX_DPR = 1.5; // bg image is 1920px wide; rendering beyond this adds cost, not detail

const QUAD_VERT = `
  attribute vec2 vertex;
  varying vec2 coord;
  void main() {
    coord = vertex * 0.5 + 0.5;
    gl_Position = vec4(vertex, 0.0, 1.0);
  }
`;

const DROP_FRAG = `
  precision highp float;
  const float PI = 3.141592653589793;
  uniform sampler2D uState;
  uniform vec2 center;
  uniform float radius;
  uniform float strength;
  varying vec2 coord;
  void main() {
    vec4 info = texture2D(uState, coord);
    float drop = max(0.0, 1.0 - length(center * 0.5 + 0.5 - coord) / radius);
    drop = 0.5 - cos(drop * PI) * 0.5;
    info.r += drop * strength;
    gl_FragColor = info;
  }
`;

// r = height, g = velocity
const UPDATE_FRAG = `
  precision highp float;
  uniform sampler2D uState;
  uniform vec2 delta;
  varying vec2 coord;
  void main() {
    vec4 info = texture2D(uState, coord);
    vec2 dx = vec2(delta.x, 0.0);
    vec2 dy = vec2(0.0, delta.y);
    float average = (
      texture2D(uState, coord - dx).r +
      texture2D(uState, coord - dy).r +
      texture2D(uState, coord + dx).r +
      texture2D(uState, coord + dy).r
    ) * 0.25;
    info.g += (average - info.r) * 2.0;
    info.g *= 0.995;
    info.r += info.g;
    gl_FragColor = info;
  }
`;

const RENDER_VERT = `
  attribute vec2 vertex;
  uniform vec2 topLeft;
  uniform vec2 bottomRight;
  uniform vec2 containerRatio;
  varying vec2 ripplesCoord;
  varying vec2 backgroundCoord;
  void main() {
    // CSS "background-size: cover" crop, image y running top → bottom
    backgroundCoord = mix(topLeft, bottomRight, vec2(vertex.x * 0.5 + 0.5, 0.5 - vertex.y * 0.5));
    // The square sim grid spans the canvas' longest side
    ripplesCoord = vertex * containerRatio * 0.5 + 0.5;
    gl_Position = vec4(vertex, 0.0, 1.0);
  }
`;

const RENDER_FRAG = `
  precision highp float;
  uniform sampler2D samplerBackground;
  uniform sampler2D samplerRipples;
  uniform vec2 delta;
  uniform float perturbance;
  varying vec2 ripplesCoord;
  varying vec2 backgroundCoord;
  void main() {
    float height = texture2D(samplerRipples, ripplesCoord).r;
    float heightX = texture2D(samplerRipples, vec2(ripplesCoord.x + delta.x, ripplesCoord.y)).r;
    float heightY = texture2D(samplerRipples, vec2(ripplesCoord.x, ripplesCoord.y + delta.y)).r;
    vec3 dx = vec3(delta.x, heightX - height, 0.0);
    vec3 dy = vec3(0.0, heightY - height, delta.y);
    vec2 offset = -normalize(cross(dy, dx)).xz;
    float specular = pow(max(0.0, dot(offset, normalize(vec2(-0.6, 1.0)))), 4.0);
    gl_FragColor = texture2D(samplerBackground, backgroundCoord + vec2(offset.x, -offset.y) * perturbance) + specular;
  }
`;

const createProgram = (gl, vertSrc, fragSrc) => {
  const compile = (type, src) => {
    const shader = gl.createShader(type);
    gl.shaderSource(shader, src);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      const log = gl.getShaderInfoLog(shader);
      gl.deleteShader(shader);
      throw new Error(`Shader compile failed: ${log}`);
    }
    return shader;
  };

  const vs = compile(gl.VERTEX_SHADER, vertSrc);
  const fs = compile(gl.FRAGMENT_SHADER, fragSrc);
  const program = gl.createProgram();
  gl.attachShader(program, vs);
  gl.attachShader(program, fs);
  gl.bindAttribLocation(program, 0, 'vertex');
  gl.linkProgram(program);
  gl.deleteShader(vs);
  gl.deleteShader(fs);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    throw new Error(`Program link failed: ${gl.getProgramInfoLog(program)}`);
  }

  const uniforms = {};
  const count = gl.getProgramParameter(program, gl.ACTIVE_UNIFORMS);
  for (let i = 0; i < count; i += 1) {
    const { name } = gl.getActiveUniform(program, i);
    uniforms[name] = gl.getUniformLocation(program, name);
  }
  return { program, uniforms };
};

const createSimTexture = (gl, size, config) => {
  const filter = config.linear ? gl.LINEAR : gl.NEAREST;
  const texture = gl.createTexture();
  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, filter);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, filter);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, size, size, 0, gl.RGBA, config.type, null);
  return texture;
};

// The simulation needs a float (or half-float) texture we can render into.
// Probe each option instead of assuming — iOS, for example, only offers half floats.
const pickTextureConfig = (gl) => {
  const candidates = [];
  if (gl.getExtension('OES_texture_float')) {
    candidates.push({ type: gl.FLOAT, linear: !!gl.getExtension('OES_texture_float_linear') });
  }
  const half = gl.getExtension('OES_texture_half_float');
  if (half) {
    candidates.push({ type: half.HALF_FLOAT_OES, linear: !!gl.getExtension('OES_texture_half_float_linear') });
  }

  return candidates.find((config) => {
    const texture = createSimTexture(gl, 32, config);
    const framebuffer = gl.createFramebuffer();
    gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffer);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, texture, 0);
    const ok = gl.checkFramebufferStatus(gl.FRAMEBUFFER) === gl.FRAMEBUFFER_COMPLETE;
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.deleteFramebuffer(framebuffer);
    gl.deleteTexture(texture);
    return ok;
  }) || null;
};

// Same math as CSS `background-size: cover; background-position: center`
const coverBounds = (cw, ch, iw, ih) => {
  const scale = Math.max(cw / iw, ch / ih);
  const dw = iw * scale;
  const dh = ih * scale;
  const left = (dw - cw) / 2 / dw;
  const top = (dh - ch) / 2 / dh;
  return [left, top, left + cw / dw, top + ch / dh];
};

/**
 * @param {React.RefObject<HTMLCanvasElement>} canvasRef  canvas to render into
 * @param {React.RefObject<HTMLElement>} targetRef        element that receives pointer input
 * @param {string} imageSrc                               background image (same origin)
 * @param {Partial<typeof DEFAULTS>} options
 * @returns {'loading' | 'ready' | 'unsupported'}
 */
const useWaterRipples = (canvasRef, targetRef, imageSrc, options = {}) => {
  const [status, setStatus] = useState('loading');
  const [generation, setGeneration] = useState(0); // bumped on WebGL context restore
  const optsRef = useRef({ ...DEFAULTS, ...options });
  optsRef.current = { ...DEFAULTS, ...options };

  useEffect(() => {
    const canvas = canvasRef.current;
    const target = targetRef.current || canvas;
    if (!canvas) return undefined;

    const gl = canvas.getContext('webgl', {
      alpha: false,
      antialias: false, // full-screen quad — MSAA would only cost fill rate
      depth: false,
      stencil: false,
      preserveDrawingBuffer: false,
      powerPreference: 'high-performance',
    }) || canvas.getContext('experimental-webgl');

    const config = gl && pickTextureConfig(gl);
    if (!config) {
      setStatus('unsupported');
      return undefined;
    }

    let programs;
    try {
      programs = {
        drop: createProgram(gl, QUAD_VERT, DROP_FRAG),
        update: createProgram(gl, QUAD_VERT, UPDATE_FRAG),
        render: createProgram(gl, RENDER_VERT, RENDER_FRAG),
      };
    } catch (err) {
      setStatus('unsupported');
      return undefined;
    }

    const { resolution } = optsRef.current;
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    // ── GPU resources ───────────────────────────────────────────────────────
    const quad = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, quad);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, 1, 1, -1, 1]), gl.STATIC_DRAW);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);

    // Ping-pong pair: each pass reads one and writes the other
    const textures = [0, 1].map(() => createSimTexture(gl, resolution, config));
    const framebuffers = textures.map((texture) => {
      const fb = gl.createFramebuffer();
      gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
      gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, texture, 0);
      return fb;
    });
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    let readIndex = 0;

    const bgTexture = gl.createTexture();
    let bgReady = false;
    let imageSize = null;

    // ── Layout state ────────────────────────────────────────────────────────
    let cssW = 1;
    let cssH = 1;
    let containerRatio = [1, 1];
    let bounds = [0, 0, 1, 1];

    const measure = () => {
      cssW = Math.max(1, canvas.clientWidth);
      cssH = Math.max(1, canvas.clientHeight);
      const dpr = Math.min(window.devicePixelRatio || 1, MAX_DPR);
      canvas.width = Math.round(cssW * dpr);
      canvas.height = Math.round(cssH * dpr);
      const longest = Math.max(cssW, cssH);
      containerRatio = [cssW / longest, cssH / longest];
      if (imageSize) bounds = coverBounds(cssW, cssH, imageSize[0], imageSize[1]);
    };
    measure();

    // ── Simulation passes ───────────────────────────────────────────────────
    const drawQuad = () => gl.drawArrays(gl.TRIANGLE_FAN, 0, 4);

    const runSimPass = ({ program, uniforms }, setUniforms) => {
      gl.viewport(0, 0, resolution, resolution);
      gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffers[1 - readIndex]);
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, textures[readIndex]);
      gl.useProgram(program);
      gl.uniform1i(uniforms.uState, 0);
      setUniforms(uniforms);
      drawQuad();
      readIndex = 1 - readIndex;
    };

    const applyDrop = ([x, y, radiusPx, strength]) => {
      const longest = Math.max(cssW, cssH);
      runSimPass(programs.drop, (u) => {
        gl.uniform2f(u.center, (2 * x - cssW) / longest, (cssH - 2 * y) / longest);
        gl.uniform1f(u.radius, radiusPx / longest);
        gl.uniform1f(u.strength, strength);
      });
    };

    const stepSimulation = () => {
      runSimPass(programs.update, (u) => {
        gl.uniform2f(u.delta, 1 / resolution, 1 / resolution);
      });
    };

    const render = () => {
      const { program, uniforms } = programs.render;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, canvas.width, canvas.height);
      gl.useProgram(program);
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, bgTexture);
      gl.activeTexture(gl.TEXTURE1);
      gl.bindTexture(gl.TEXTURE_2D, textures[readIndex]);
      gl.uniform1i(uniforms.samplerBackground, 0);
      gl.uniform1i(uniforms.samplerRipples, 1);
      gl.uniform2f(uniforms.topLeft, bounds[0], bounds[1]);
      gl.uniform2f(uniforms.bottomRight, bounds[2], bounds[3]);
      gl.uniform2f(uniforms.containerRatio, containerRatio[0], containerRatio[1]);
      gl.uniform2f(uniforms.delta, 1 / resolution, 1 / resolution);
      gl.uniform1f(uniforms.perturbance, optsRef.current.perturbance);
      drawQuad();
    };

    // ── Input ───────────────────────────────────────────────────────────────
    // Drops are queued and applied inside the frame loop, so GL work never runs
    // inside event handlers and a burst of events can't stall the main thread.
    const queue = [];
    let prevPoint = null;
    let lastInteraction = -Infinity;
    let visible = false;

    const enqueue = (drop) => { if (queue.length < MAX_QUEUED_DROPS) queue.push(drop); };

    const dropAt = (clientX, clientY, big) => {
      if (!visible) return;
      const rect = canvas.getBoundingClientRect();
      const x = clientX - rect.left;
      const y = clientY - rect.top;
      if (x < 0 || y < 0 || x > rect.width || y > rect.height) return;

      const { dropRadius, moveStrength, clickStrength } = optsRef.current;
      const now = performance.now();

      if (big) {
        enqueue([x, y, dropRadius * 1.5, clickStrength]);
      } else {
        // Browsers coalesce mousemove to ~1 event per frame, so a fast swipe
        // leaves gaps of 50px+ between samples. Fill them so the wake is one
        // continuous trail instead of a dotted line.
        if (prevPoint && now - prevPoint.t < 100) {
          const dx = x - prevPoint.x;
          const dy = y - prevPoint.y;
          const gaps = Math.min(Math.floor(Math.hypot(dx, dy) / (dropRadius * 0.75)), 10);
          for (let i = 1; i <= gaps; i += 1) {
            const f = i / (gaps + 1);
            enqueue([prevPoint.x + dx * f, prevPoint.y + dy * f, dropRadius, moveStrength]);
          }
        }
        enqueue([x, y, dropRadius, moveStrength]);
        prevPoint = { x, y, t: now };
      }
      lastInteraction = now;
    };

    const onMouseMove = (e) => dropAt(e.clientX, e.clientY, false);
    const onMouseDown = (e) => dropAt(e.clientX, e.clientY, true);
    const onMouseLeave = () => { prevPoint = null; };
    const onTouchStart = (e) => {
      Array.from(e.changedTouches).forEach((t) => dropAt(t.clientX, t.clientY, true));
    };
    const onTouchMove = (e) => {
      Array.from(e.changedTouches).forEach((t) => dropAt(t.clientX, t.clientY, false));
    };

    target.addEventListener('mousemove', onMouseMove, { passive: true });
    target.addEventListener('mousedown', onMouseDown, { passive: true });
    target.addEventListener('mouseleave', onMouseLeave, { passive: true });
    target.addEventListener('touchstart', onTouchStart, { passive: true });
    target.addEventListener('touchmove', onTouchMove, { passive: true });

    // ── Frame loop — only runs while the section is on screen ──────────────
    let raf = 0;
    let lastTime = 0;
    let accumulator = 0;
    let nextAmbient = 0;

    const frame = (now) => {
      raf = requestAnimationFrame(frame);
      accumulator += Math.min(now - lastTime, 100);
      lastTime = now;

      if (optsRef.current.ambient && !reduceMotion
        && now - lastInteraction > 2500 && now > nextAmbient) {
        const { dropRadius } = optsRef.current;
        enqueue([Math.random() * cssW, Math.random() * cssH, dropRadius * 1.5, 0.06]);
        nextAmbient = now + 1800 + Math.random() * 1600;
      }

      queue.splice(0).forEach(applyDrop);

      let steps = 0;
      while (accumulator >= STEP_MS && steps < MAX_STEPS_PER_FRAME) {
        stepSimulation();
        accumulator -= STEP_MS;
        steps += 1;
      }
      if (steps === MAX_STEPS_PER_FRAME) accumulator = 0; // don't spiral after a stall

      if (bgReady) render();
    };

    const start = () => {
      if (raf) return;
      lastTime = performance.now();
      accumulator = 0;
      raf = requestAnimationFrame(frame);
    };
    const stop = () => {
      cancelAnimationFrame(raf);
      raf = 0;
      queue.length = 0;
      prevPoint = null;
    };

    const intersection = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      if (visible) start(); else stop();
    }, { threshold: 0 });
    intersection.observe(canvas);

    const resize = new ResizeObserver(() => { measure(); if (bgReady) render(); });
    resize.observe(canvas);

    // ── Background image ────────────────────────────────────────────────────
    const image = new Image();
    image.decoding = 'async';
    image.onload = () => {
      gl.bindTexture(gl.TEXTURE_2D, bgTexture);
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image);
      imageSize = [image.naturalWidth, image.naturalHeight];
      bounds = coverBounds(cssW, cssH, imageSize[0], imageSize[1]);
      bgReady = true;
      render();
      setStatus('ready');
    };
    image.onerror = () => setStatus('unsupported');
    image.src = imageSrc;

    // ── Context loss (GPU reset, driver update, too many contexts) ─────────
    const onContextLost = (e) => {
      e.preventDefault();
      stop();
      setStatus('loading');
    };
    const onContextRestored = () => setGeneration((g) => g + 1);
    canvas.addEventListener('webglcontextlost', onContextLost);
    canvas.addEventListener('webglcontextrestored', onContextRestored);

    return () => {
      stop();
      intersection.disconnect();
      resize.disconnect();
      image.onload = null;
      image.onerror = null;
      target.removeEventListener('mousemove', onMouseMove);
      target.removeEventListener('mousedown', onMouseDown);
      target.removeEventListener('mouseleave', onMouseLeave);
      target.removeEventListener('touchstart', onTouchStart);
      target.removeEventListener('touchmove', onTouchMove);
      canvas.removeEventListener('webglcontextlost', onContextLost);
      canvas.removeEventListener('webglcontextrestored', onContextRestored);

      if (!gl.isContextLost()) {
        Object.values(programs).forEach(({ program }) => gl.deleteProgram(program));
        textures.forEach((t) => gl.deleteTexture(t));
        framebuffers.forEach((fb) => gl.deleteFramebuffer(fb));
        gl.deleteTexture(bgTexture);
        gl.deleteBuffer(quad);
      }
    };
  }, [canvasRef, targetRef, imageSrc, generation]);

  return status;
};

export default useWaterRipples;
